import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  User, 
  Save, 
  Star, 
  CheckCircle, 
  AlertCircle,
  BookOpen,
  Briefcase,
  Settings,
  Heart,
  Award,
  Upload,
  X,
  Camera,
  Loader2,
  Calendar
} from 'lucide-react';
import { UserProfile, ProfileField, ProfileCategory } from '@/types/profile';
import { ProfileStrengthCard } from './profile-strength-card';
import { createProfileStrengthData } from '@/utils/profile-strength';
import { getProfileCategories } from '@/config/profile-fields';
import { getUserInitials } from '@/utils/user-profile';
import { AuthUser } from '@/types/auth';
import { StorageService } from '@/services/storage-service';
import { useAuth } from '@/components/auth/AuthProvider';

const storageService = new StorageService();

/**
 * Gets the best available avatar URL for profile form display
 */
function getFormAvatarUrl(value: any, user: AuthUser | null): string | undefined {
  // First priority: form value (uploaded or existing)
  if (value) {
    return value;
  }
  
  if (!user) return undefined;
  
  // Fallback to auth metadata for initial display
  if (user.user_metadata?.avatar_url) {
    return user.user_metadata.avatar_url;
  }
  if (user.user_metadata?.picture) {
    return user.user_metadata.picture;
  }
  if (user.user_metadata?.avatar) {
    return user.user_metadata.avatar;
  }
  if (user.user_metadata?.photo) {
    return user.user_metadata.photo;
  }
  
  // Check Google identity data
  const googleIdentity = user.identities?.find((id: any) => id.provider === 'google');
  if (googleIdentity?.identity_data) {
    try {
      const identityData = typeof googleIdentity.identity_data === 'string' 
        ? JSON.parse(googleIdentity.identity_data) 
        : googleIdentity.identity_data;
      
      if (identityData.picture) {
        return identityData.picture;
      }
      if (identityData.avatar_url) {
        return identityData.avatar_url;
      }
      if (identityData.photo) {
        return identityData.photo;
      }
      if (identityData.image) {
        return identityData.image;
      }
    } catch (error) {
      console.error('ProfileForm: Error parsing Google identity_data:', error);
    }
  }
  
  return undefined;
}

// Enhanced learning preference display utilities
const getLearningStyleInfo = (style?: string) => {
  const styles: Record<string, { name: string; description: string; icon: string }> = {
    'Visual (diagrams, charts)': {
      name: 'Visual Learner',
      description: 'You learn best through diagrams, charts, and visual aids',
      icon: '👁️'
    },
    'Auditory (listening, discussions)': {
      name: 'Auditory Learner', 
      description: 'You prefer listening to lectures and participating in discussions',
      icon: '👂'
    },
    'Kinesthetic (hands-on activities)': {
      name: 'Kinesthetic Learner',
      description: 'You learn best through hands-on activities and movement',
      icon: '✋'
    },
    'Reading/Writing (text-based)': {
      name: 'Reading/Writing Learner',
      description: 'You prefer reading and writing to absorb information',
      icon: '📚'
    }
  };
  return style ? styles[style] : null;
};

const getStudyTimeInfo = (schedule?: string) => {
  const schedules: Record<string, { name: string; description: string; icon: string }> = {
    'Early Morning (6-9 AM)': {
      name: 'Early Bird',
      description: 'You\'re most productive in the early morning hours',
      icon: '🌅'
    },
    'Morning (9-12 PM)': {
      name: 'Morning Person',
      description: 'You focus best during morning hours',
      icon: '☀️'
    },
    'Afternoon (12-4 PM)': {
      name: 'Afternoon Learner',
      description: 'You study effectively in the afternoon',
      icon: '🌤️'
    },
    'Evening (4-8 PM)': {
      name: 'Evening Studier',
      description: 'You prefer studying in the evening hours',
      icon: '🌆'
    },
    'Night (8-11 PM)': {
      name: 'Night Owl',
      description: 'You\'re most focused during nighttime study sessions',
      icon: '🌙'
    }
  };
  return schedule ? schedules[schedule] : null;
};

interface ProfileFormProps {
  profile: UserProfile;
  userType: 'student' | 'teacher';
  onSave: (profile: UserProfile) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  initialTab?: string;
  highlightField?: string;
}

export function ProfileForm({ 
  profile, 
  userType, 
  onSave, 
  onCancel,
  isLoading = false,
  initialTab,
  highlightField
}: ProfileFormProps) {
  // Ensure profile has proper nested structure for student/teacher details
  const initializeProfileStructure = (profile: UserProfile): UserProfile => {
    const initialized = { ...profile };
    
    // Initialize student_details if user is student
    if (userType === 'student') {
      initialized.student_details = {
        class_grade: undefined,
        school_name: undefined,
        academic_interests: [],
        learning_goals: [],
        learning_style: undefined,
        preferred_subjects: [],
        study_schedule: undefined,
        achievements: [],
        extracurricular_activities: [],
        // Preserve existing values if they exist
        ...initialized.student_details
      };
    }
    
    // Initialize teacher_details if user is teacher
    if (userType === 'teacher') {
      initialized.teacher_details = {
        qualifications: [],
        years_of_experience: undefined,
        subjects_teaching: [],
        institution_name: undefined,
        teaching_style: [],
        specializations: [],
        certifications: [],
        awards: [],
        publications: [],
        professional_development: [],
        // Preserve existing values if they exist
        ...initialized.teacher_details
      };
    }
    
    return initialized;
  };

  const [formData, setFormData] = useState<UserProfile>(initializeProfileStructure(profile));
  const [activeTab, setActiveTab] = useState<string>(initialTab || 'basic_info');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const { user } = useAuth();

  const categories = getProfileCategories(userType);
  const strengthData = createProfileStrengthData(formData, userType);

  useEffect(() => {
    setFormData(initializeProfileStructure(profile));
  }, [profile, userType]);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Clear highlight after a few seconds
  useEffect(() => {
    if (highlightField) {
      const timer = setTimeout(() => {
        // Don't clear in this component to avoid prop drilling
        // Let parent handle clearing via save or cancel
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightField]);

  const getCategoryIcon = (categoryKey: string) => {
    switch (categoryKey) {
      case 'basic_info':
        return <User className="w-4 h-4" />;
      case 'academic_info':
      case 'professional_info':
        return <BookOpen className="w-4 h-4" />;
      case 'preferences':
      case 'teaching_profile':
        return <Settings className="w-4 h-4" />;
      case 'personal_info':
        return <Heart className="w-4 h-4" />;
      case 'additional_info':
        return <Award className="w-4 h-4" />;
      default:
        return <User className="w-4 h-4" />;
    }
  };

  // Helper functions for date formatting
  const formatDateToDisplay = (dateString: string): string => {
    if (!dateString) return '';
    
    // If already in DD-MM-YYYY format, return as is
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      return dateString;
    }
    
    // If in YYYY-MM-DD format (from date input), convert to DD-MM-YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${day}-${month}-${year}`;
    }
    
    return dateString;
  };

  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    
    // If in DD-MM-YYYY format, convert to YYYY-MM-DD for input
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('-');
      return `${year}-${month}-${day}`;
    }
    
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    return '';
  };

  const validateDateFormat = (dateString: string): boolean => {
    if (!dateString) return true; // Empty is valid for non-required fields
    
    // Check DD-MM-YYYY format
    const ddmmyyyyRegex = /^\d{2}-\d{2}-\d{4}$/;
    if (!ddmmyyyyRegex.test(dateString)) {
      return false;
    }
    
    // Parse and validate the actual date
    const [day, month, year] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    return date.getFullYear() === year &&
           date.getMonth() === month - 1 &&
           date.getDate() === day;
  };

  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
  };

  const setNestedValue = (obj: any, path: string, value: any) => {
    const keys = path.split('.');
    const lastKey = keys.pop()!;
    const target = keys.reduce((current, key) => {
      if (!current[key] || typeof current[key] !== 'object') {
        current[key] = {};
      }
      return current[key];
    }, obj);
    target[lastKey] = value;
  };

  const handleFieldChange = (field: ProfileField, value: any) => {
    const newFormData = { ...formData };
    setNestedValue(newFormData, field.key, value);
    setFormData(newFormData);

    // Clear any existing error for this field
    if (errors[field.key]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field.key];
        return newErrors;
      });
    }
  };

  /**
   * Handle avatar upload to storage service
   */
  const handleAvatarUpload = async (file: File): Promise<void> => {
    if (!user?.id) {
      console.error('ProfileForm: No user ID available for avatar upload');
      return;
    }

    setIsUploadingAvatar(true);
    
    try {
      // Upload avatar using the storage service
      const result = await storageService.completeAvatarUpload(user.id, file);
      
      if (result.success && result.url) {
        // Update the form data with the new avatar URL
        const avatarField: ProfileField = {
          key: 'avatar_url',
          label: 'Profile Picture',
          category: 'personal_info',
          weight: 6,
          required: false,
          type: 'avatar'
        };
        
        handleFieldChange(avatarField, result.url);
      } else {
        console.error('ProfileForm: Avatar upload failed:', result.error);
        alert(`Avatar upload failed: ${result.error}`);
      }
    } catch (error) {
      console.error('ProfileForm: Exception during avatar upload:', error);
      alert('Avatar upload failed. Please try again.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  /**
   * Handle avatar removal from user metadata
   */
  const handleAvatarRemoval = async (): Promise<void> => {
    setIsUploadingAvatar(true);
    
    try {
      // Remove avatar using the storage service
      const result = await storageService.completeAvatarRemoval();
      
      if (result.success) {
        // Update the form data to clear the avatar URL
        const avatarField: ProfileField = {
          key: 'avatar_url',
          label: 'Profile Picture',
          category: 'personal_info',
          weight: 6,
          required: false,
          type: 'avatar'
        };
        
        handleFieldChange(avatarField, '');
      } else {
        console.error('ProfileForm: Avatar removal failed:', result.error);
        alert(`Avatar removal failed: ${result.error}`);
      }
    } catch (error) {
      console.error('ProfileForm: Exception during avatar removal:', error);
      alert('Avatar removal failed. Please try again.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const validateField = (field: ProfileField, value: any): string | null => {
    if (field.required && (!value || (typeof value === 'string' && !value.trim()))) {
      const error = `${field.label} is required`;
      return error;
    }

    if (field.type === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return 'Please enter a valid email address';
    }

    if (field.type === 'tel' && value && !/^\+?[\d\s\-\(\)]+$/.test(value)) {
      return 'Please enter a valid phone number';
    }

    // Special validation for date fields
    if (field.type === 'date' && value) {
      // Check DD-MM-YYYY format
      const ddmmyyyyRegex = /^\d{2}-\d{2}-\d{4}$/;
      if (!ddmmyyyyRegex.test(value)) {
        return 'Please enter date in DD-MM-YYYY format';
      }
      
      // Parse and validate the actual date
      const [day, month, year] = value.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      
      // Check if the date is valid
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return 'Please enter a valid date';
      }
      
      // For date of birth, check if it's not in the future
      if (field.key === 'date_of_birth' && date > new Date()) {
        return 'Date of birth cannot be in the future';
      }
      
      // Check for reasonable date of birth range (e.g., not older than 120 years)
      if (field.key === 'date_of_birth') {
        const minDate = new Date();
        minDate.setFullYear(minDate.getFullYear() - 120);
        if (date < minDate) {
          return 'Please enter a valid date of birth';
        }
      }
    }

    return null;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    categories.forEach(category => {
      category.fields.forEach(field => {
        const value = getNestedValue(formData, field.key);
        const error = validateField(field, value);
        if (error) {
          newErrors[field.key] = error;
          hasErrors = true;
        }
      });
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      // Save the profile data - this will include the avatar_url
      await onSave(formData);
      
      // Note: The avatar URL is already included in formData.avatar_url
      // The onSave callback (in ProfilePage) will handle updating the backend
      // via the ProfileContext which calls profileService.updateProfile()
      
    } catch (error) {
      console.error('ProfileForm: Error saving profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const renderField = (field: ProfileField) => {
    const value = getNestedValue(formData, field.key);
    const error = errors[field.key];
    
    // Use the same field completion logic as profile strength calculation
    const isCompleted = (() => {
      if (value === null || value === undefined) return false;
      
      switch (field.type) {
        case 'text':
        case 'email':
        case 'tel':
        case 'textarea':
        case 'date':
        case 'select':
          return typeof value === 'string' && value.trim().length > 0;
        
        case 'number':
          return typeof value === 'number' && value >= 0;
        
        case 'multiselect':
          return Array.isArray(value) && value.length > 0;
        
        default:
          return false;
      }
    })();
    
    const isHighlighted = highlightField === field.key;

    const fieldWrapper = (children: React.ReactNode) => (
      <div className={`space-y-2 transition-all duration-300 ${
        isHighlighted 
          ? 'p-3 rounded-lg border-2 border-primary bg-primary/5' 
          : ''
      }`}>
        <div className="flex items-center space-x-2">
          <Label htmlFor={field.key} className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {isCompleted && (
            <CheckCircle className="w-4 h-4 text-green-600" />
          )}
          {isHighlighted && (
            <Badge variant="secondary" className="text-xs">
              Quick Win
            </Badge>
          )}
        </div>
        {children}
        {field.description && (
          <p className="text-xs text-muted-foreground">{field.description}</p>
        )}
        {error && (
          <div className="flex items-center space-x-1 text-red-600">
            <AlertCircle className="w-3 h-3" />
            <span className="text-xs">{error}</span>
          </div>
        )}
      </div>
    );

    // Special case for avatar_url - show profile picture component
    if (field.key === 'avatar_url') {
      return fieldWrapper(
        <div className="flex flex-col items-center space-y-4">
          {/* Avatar Display */}
          <div className="relative group">
            <Avatar className="w-24 h-24 border-2 border-border shadow-sm">
              {getFormAvatarUrl(value, user) && (
                <AvatarImage 
                  src={getFormAvatarUrl(value, user)} 
                  alt="Profile picture"
                  className="object-cover"
                />
              )}
              <AvatarFallback className="text-2xl bg-muted">
                {user ? getUserInitials(user) : 'U'}
              </AvatarFallback>
            </Avatar>
            
            {/* Upload overlay on hover */}
            <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center cursor-pointer"
                 onClick={() => {
                   if (isUploadingAvatar) return;
                   
                   const input = document.createElement('input');
                   input.type = 'file';
                   input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
                   input.onchange = async (e) => {
                     const file = (e.target as HTMLInputElement).files?.[0];
                     if (file) {
                       // Validate file size (max 2MB)
                       if (file.size > 2 * 1024 * 1024) {
                         alert('Please select an image smaller than 2MB');
                         return;
                       }
                       
                       await handleAvatarUpload(file);
                     }
                   };
                   input.click();
                 }}
            >
              {isUploadingAvatar ? (
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              ) : (
                <Camera className="w-6 h-6 text-white" />
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (isUploadingAvatar) return;
                
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
                input.onchange = async (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    // Validate file size (max 2MB)
                    if (file.size > 2 * 1024 * 1024) {
                      alert('Please select an image smaller than 2MB');
                      return;
                    }
                    
                    await handleAvatarUpload(file);
                  }
                };
                input.click();
              }}
              className="flex items-center space-x-2"
              disabled={isUploadingAvatar}
            >
              {isUploadingAvatar ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>{value ? 'Change' : 'Upload'}</span>
                </>
              )}
            </Button>

            {value && !isUploadingAvatar && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAvatarRemoval}
                className="text-muted-foreground hover:text-destructive"
              >
                Remove
              </Button>
            )}
          </div>
          
          {/* Upload Instructions */}
          <p className="text-xs text-muted-foreground text-center max-w-xs">
            Upload a professional photo. Supported formats: JPG, PNG, WebP. Maximum size: 2MB.
          </p>
        </div>
      );
    }

    // Special case for date fields (especially date_of_birth)
    if (field.type === 'date') {
      return fieldWrapper(
        <div className="space-y-2">
          <div className="relative">
            <Input
              id={field.key}
              type="date"
              value={formatDateForInput(value)}
              onChange={(e) => {
                const inputDate = e.target.value; // YYYY-MM-DD format from input
                if (inputDate) {
                  // Convert to DD-MM-YYYY format for storage
                  const formattedDate = formatDateToDisplay(inputDate);
                  handleFieldChange(field, formattedDate);
                } else {
                  handleFieldChange(field, '');
                }
              }}
              className={`${error ? 'border-red-500' : ''} pr-10`}
              max={new Date().toISOString().split('T')[0]} // Prevent future dates for DOB
            />
            <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          </div>
        </div>
      );
    }

    switch (field.type) {
      case 'textarea':
        return fieldWrapper(
          <Textarea
            id={field.key}
            value={value || ''}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            placeholder={field.placeholder}
            className={error ? 'border-red-500' : ''}
            rows={3}
          />
        );

      case 'select':
        // Use searchable combobox for school/institution fields
        const isSearchableField = field.key === 'student_details.school_name' || 
                                 field.key === 'teacher_details.institution_name';
        
        if (isSearchableField) {
          return fieldWrapper(
            <Combobox
              options={field.options || []}
              value={value || ''}
              onValueChange={(newValue) => handleFieldChange(field, newValue)}
              placeholder={field.placeholder || 'Select an option'}
              emptyText="No matches found. Try a different search term."
              className={error ? 'border-red-500' : ''}
            />
          );
        }
        
        // Enhanced learning preferences handling
        const isLearningStyle = field.key === 'student_details.learning_style';
        const isStudySchedule = field.key === 'student_details.study_schedule';
        const isClassGrade = field.key === 'student_details.class_grade';
        
        if (isLearningStyle || isStudySchedule) {
          // Debug logging for learning preferences
          
          return fieldWrapper(
            <div className="space-y-3">
              <Select
                value={value || ''}
                onValueChange={(newValue) => handleFieldChange(field, newValue)}
              >
                <SelectTrigger className={error ? 'border-red-500' : ''}>
                  <SelectValue placeholder={field.placeholder || 'Select an option'} />
                </SelectTrigger>
                <SelectContent>
                  {field.options?.map((option) => {
                    const info = isLearningStyle ? getLearningStyleInfo(option) : getStudyTimeInfo(option);
                    return (
                      <SelectItem key={option} value={option}>
                        <div className="flex items-center gap-2 py-1">
                          <span className="text-base">{info?.icon || '📝'}</span>
                          <div className="flex flex-col">
                            <span className="font-medium">{info?.name || option}</span>
                            {info?.description && (
                              <span className="text-xs text-muted-foreground leading-tight">
                                {info.description}
                              </span>
                            )}
                          </div>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          );
        }
        
        // Special handling for class/grade - disable only if already set
        if (isClassGrade) {
          const hasExistingClassGrade = Boolean(profile.student_details?.class_grade);
          
          return fieldWrapper(
            <div className="space-y-2">
              {hasExistingClassGrade ? (
                // Show a disabled input-like display when class is already set
                <div className="flex items-center justify-between p-3 border rounded-md bg-muted/50 opacity-60">
                  <span className="text-sm text-foreground">
                    Class {value}
                  </span>
                  <Badge variant="secondary" className="text-xs">
                    Set
                  </Badge>
                </div>
              ) : (
                // Show normal select when class is not set
                <Select
                  value={value ? String(value) : ''}
                  onValueChange={(newValue) => handleFieldChange(field, newValue)}
                >
                  <SelectTrigger className={error ? 'border-red-500' : ''}>
                    <SelectValue placeholder={field.placeholder || 'Select your class/grade'} />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options?.map((option) => (
                      <SelectItem key={option} value={String(option)}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {hasExistingClassGrade ? (
                <p className="text-xs text-muted-foreground">
                  Class/Grade cannot be changed after it has been set. Contact support if you need to update this.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Select your current class/grade. This cannot be changed once set.
                </p>
              )}
            </div>
          );
        }
        
        // Regular select for other fields
        return fieldWrapper(
          <Select
            value={value || ''}
            onValueChange={(newValue) => handleFieldChange(field, newValue)}
          >
            <SelectTrigger className={error ? 'border-red-500' : ''}>
              <SelectValue placeholder={field.placeholder || 'Select an option'} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'multiselect':
        return fieldWrapper(
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2 mb-2">
              {(value || []).map((selectedOption: string) => (
                <Badge key={selectedOption} variant="secondary" className="text-xs">
                  {selectedOption}
                  <button
                    type="button"
                    onClick={() => {
                      const newValue = (value || []).filter((v: string) => v !== selectedOption);
                      handleFieldChange(field, newValue);
                    }}
                    className="ml-1 hover:text-destructive"
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {field.options?.map((option) => (
                <div key={option} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${field.key}-${option}`}
                    checked={(value || []).includes(option)}
                    onCheckedChange={(checked: boolean) => {
                      const currentValue = value || [];
                      const newValue = checked
                        ? [...currentValue, option]
                        : currentValue.filter((v: string) => v !== option);
                      handleFieldChange(field, newValue);
                    }}
                  />
                  <Label
                    htmlFor={`${field.key}-${option}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {option}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        );

      case 'number':
        return fieldWrapper(
          <Input
            id={field.key}
            type="number"
            value={value || ''}
            onChange={(e) => handleFieldChange(field, parseInt(e.target.value) || 0)}
            placeholder={field.placeholder}
            className={error ? 'border-red-500' : ''}
          />
        );

      case 'avatar':
        // This case is handled above by the special avatar_url check
        return null;

      default:
        return fieldWrapper(
          <Input
            id={field.key}
            type={field.type}
            value={value || ''}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            placeholder={field.placeholder}
            className={error ? 'border-red-500' : ''}
          />
        );
    }
  };

  const getCategoryCompletion = (category: ProfileCategory) => {
    let completed = 0;
    category.fields.forEach(field => {
      const value = getNestedValue(formData, field.key);
      
      // Use consistent field completion logic
      const isFieldCompleted = (() => {
        if (value === null || value === undefined) return false;
        
        switch (field.type) {
          case 'text':
          case 'email':
          case 'tel':
          case 'textarea':
          case 'date':
          case 'select':
            return typeof value === 'string' && value.trim().length > 0;
          
          case 'number':
            return typeof value === 'number' && value >= 0;
          
          case 'multiselect':
            return Array.isArray(value) && value.length > 0;
          
          case 'avatar':
            return typeof value === 'string' && value.trim().length > 0;
          
          default:
            return false;
        }
      })();
      
      if (isFieldCompleted) {
        completed++;
      }
    });
    return (completed / category.fields.length) * 100;
  };

  const getProgressColorClass = (value: number): string => {
    if (value < 30) return 'bg-red-500';
    if (value < 70) return 'bg-orange-500';
    return 'bg-green-500';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Edit Profile</h2>
          <p className="text-muted-foreground">
            Complete your profile to unlock more features and better recommendations.
          </p>
        </div>
        <div className="text-right">
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-sm font-medium">Overall Progress</span>
            <Badge variant="secondary">
              {Math.floor(strengthData.totalStrength)}%
            </Badge>
          </div>
          <Progress 
            value={strengthData.totalStrength} 
            className="w-32 h-2" 
            indicatorClassName={getProgressColorClass(strengthData.totalStrength)}
          />
        </div>
      </div>

      {/* Form */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 lg:grid-cols-5">
          {categories.map((category) => {
            const completion = getCategoryCompletion(category);
            return (
              <TabsTrigger 
                key={category.key} 
                value={category.key}
                className="flex items-center space-x-1 text-xs"
              >
                {getCategoryIcon(category.key)}
                <span className="hidden sm:inline">{category.title}</span>
                <div className="w-2 h-2 rounded-full border border-current">
                  {completion >= 80 && (
                    <div className="w-full h-full bg-green-600 rounded-full" />
                  )}
                </div>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {categories.map((category) => (
          <TabsContent key={category.key} value={category.key}>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {getCategoryIcon(category.key)}
                    <CardTitle>{category.title}</CardTitle>
                  </div>
                  <Badge variant="outline">
                    {Math.floor(getCategoryCompletion(category))}% Complete
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {category.description}
                </p>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {category.fields.map((field) => (
                    <div 
                      key={field.key}
                      className={field.type === 'textarea' ? 'md:col-span-2' : ''}
                    >
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Actions */}
      <div className="flex items-center justify-between pt-6 border-t">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Star className="w-4 h-4" />
          <span>
            {strengthData.completedFields.length} of {strengthData.completedFields.length + strengthData.pendingFields.length} fields completed
          </span>
        </div>
        
        <div className="flex space-x-3">
          {onCancel && (
            <Button variant="outline" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
          )}
          
          <Button 
            onClick={handleSave} 
            disabled={isSaving || isLoading || isUploadingAvatar}
            className="min-w-[100px]"
          >
            {isSaving ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border border-white border-t-transparent" />
                <span>Saving...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Save className="w-4 h-4" />
                <span>Save Profile</span>
              </div>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
